import { useLazyGetInsurancePlansQuery } from '@redux/queries/claimRecords'
import { ClaimRecord } from '@typesApp/ClaimRecord'
import { Logger } from '@utils/logger'
import { getInsuranceMemberPlanActiveBenefit, isInsuranceMemberFoundStatus } from '@utils/insuranceMemberUtil'
import { useClaimRecord } from '@hooks/useClaimRecord'
import { InsurancePlan } from '@typesApp/InsurancePlan'
import { CommerceBenefitPlanResponse } from '../types'

const PREFER_CACHE = true

const EMPTY_RESPONSE = {} as CommerceBenefitPlanResponse

const UNAVAILABLE_RESPONSE = {
  status: 'unavailable',
} as CommerceBenefitPlanResponse

type UseBenefitPlanServiceResult = {
  getBenefitPlan: () => Promise<CommerceBenefitPlanResponse>
}

/**
 * @returns simplified benefit plan data for commerce F/E
 */
export const useBenefitPlanService = (): UseBenefitPlanServiceResult => {
  const { fetchClaimRecord } = useClaimRecord()
  const [triggeFetchPlans] = useLazyGetInsurancePlansQuery()

  const getBenefitPlan = (): Promise<CommerceBenefitPlanResponse> => {
    return new Promise((resolve, reject) => {
      fetchClaimRecord(PREFER_CACHE)
        .then(claimRecord => {
          if (!claimRecord) {
            resolve(UNAVAILABLE_RESPONSE)
            return
          }

          if (!isInsuranceMemberFoundStatus(claimRecord?.status)) {
            resolve(UNAVAILABLE_RESPONSE)
            return
          }

          getResponse(claimRecord, resolve)
        })
        .catch(_error => {
          resolve(EMPTY_RESPONSE)
        })
    })
  }

  const getResponse = (claimRecord: ClaimRecord, resolve: (data: CommerceBenefitPlanResponse) => void) => {
    triggeFetchPlans({ claimRecordId: claimRecord.id }, PREFER_CACHE)
      .unwrap()
      .then(plans => {
        resolve(transformResponse(plans))
      })
      .catch(error => {
        Logger.warn('Error fetching benefit plans', error)
        resolve(EMPTY_RESPONSE)
      })
  }

  const transformResponse = (plans?: InsurancePlan[]): CommerceBenefitPlanResponse => {
    const benefits = (plans ?? []).map(plan => {
      const activeBenefit = getInsuranceMemberPlanActiveBenefit(plan)
      return {
        planType: plan.planType,
        benefitStatus: activeBenefit?.benefitStatus ?? 'BENEFITS_NOT_AVAILABLE',
      }
    })

    return {
      status: benefits.length ? 'available' : 'unavailable',
      benefits,
    }
  }

  return {
    getBenefitPlan,
  }
}
